import { EMPTY_LIST, EMPTY_STRING } from '../../../../constants/common'
import { FindState, PdfViewerFindType } from '../../PdfViewer.constants'

import {
  PDF_VIEWER_SEARCH_RESULTS_LIMIT,
  PDF_VIEWER_SEARCH_SNIPPET_LENGTH,
  PDF_VIEWER_SEARCH_TEXT_CONTENT_PARAMS,
} from './usePdfViewerSearch.constants'

// the match offsets index the raw page text, not the normalized one the find
// controller keeps for itself
async function readPdfViewerPageText({ pageIndex, pdfDocument }) {
  try {
    const page = await pdfDocument.getPage(pageIndex + 1)
    const { items } = await page.getTextContent(
      PDF_VIEWER_SEARCH_TEXT_CONTENT_PARAMS
    )

    return items.map(item => item.str).join(EMPTY_STRING)
  } catch {
    return EMPTY_STRING
  }
}

function getPdfViewerPageSearchResults({
  matches,
  matchesLength,
  pageIndex,
  pageText,
}) {
  return matches.map((matchStart, matchIndex) => {
    const matchEnd = matchStart + (matchesLength?.[matchIndex] ?? 0)

    return {
      id: getPdfViewerSearchResultId({ matchIndex, pageIndex }),
      match: pageText.slice(matchStart, matchEnd),
      matchIndex,
      pageIndex,
      pageNumber: pageIndex + 1,
      suffix: pageText.slice(
        matchEnd,
        matchEnd + PDF_VIEWER_SEARCH_SNIPPET_LENGTH
      ),
    }
  })
}

export function checkIsNewPdfViewerFind(type) {
  return type === PdfViewerFindType.New
}

export function checkIsPdfViewerFindPending(state) {
  return state === FindState.PENDING
}

export function getPdfViewerSearchResultId({ matchIndex, pageIndex }) {
  return `${pageIndex}-${matchIndex}`
}

export function getPdfViewerMatchedPageIndexes(findController) {
  const pageMatches = findController?.pageMatches

  if (!pageMatches) {
    return EMPTY_LIST
  }

  return pageMatches.reduce(
    (matchedPages, matches, pageIndex) => {
      if (
        matches?.length > 0 &&
        matchedPages.matchesCount < PDF_VIEWER_SEARCH_RESULTS_LIMIT
      ) {
        matchedPages.matchesCount += matches.length

        matchedPages.pageIndexes.push(pageIndex)
      }

      return matchedPages
    },
    { matchesCount: 0, pageIndexes: [] }
  ).pageIndexes
}

export async function loadPdfViewerPageTexts({
  pageIndexes,
  pageTextPromises,
  pdfDocument,
}) {
  const pageTexts = await Promise.all(
    pageIndexes.map(pageIndex => {
      // cached as a promise: one search reports its progress many times over
      if (!pageTextPromises.has(pageIndex)) {
        pageTextPromises.set(
          pageIndex,
          readPdfViewerPageText({ pageIndex, pdfDocument })
        )
      }

      return pageTextPromises.get(pageIndex)
    })
  )

  return new Map(
    pageIndexes.map((pageIndex, index) => [pageIndex, pageTexts[index]])
  )
}

export function getPdfViewerSearchResults({ findController, pageTexts }) {
  const pageMatches = findController?.pageMatches

  if (!pageMatches) {
    return EMPTY_LIST
  }

  return pageMatches
    .flatMap((matches, pageIndex) => {
      const pageText = pageTexts.get(pageIndex)

      return pageText
        ? getPdfViewerPageSearchResults({
            matches,
            matchesLength: findController.pageMatchesLength[pageIndex],
            pageIndex,
            pageText,
          })
        : EMPTY_LIST
    })
    .slice(0, PDF_VIEWER_SEARCH_RESULTS_LIMIT)
}

export function rewindPdfViewerMatchCursor({
  findController,
  matchIndex,
  pageIndex,
}) {
  const offset = findController._offset

  offset.pageIdx = pageIndex
  offset.matchIdx = matchIndex > 0 ? matchIndex - 1 : null
  offset.wrapped = false
}
